import { Text, VStack, Box, Flex, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';

import useCurrencyFormatter from '../hooks/useCurrencyFormatter';

export interface DetailItem {
  parentId: string;
  description: string;
  dueDate: string;
  outstandingValue: number;
  penaltyValue: number;
}

interface PaymentDetailsContentProps {
  details: DetailItem[];
}

const MobileRow = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <Flex justify="space-between" py={1}>
    <Text fontWeight="semibold" color="gray.700" fontSize="0.875rem" flexShrink={0} mr={2}>
      {label}
    </Text>
    <Text color={color ?? 'gray.600'} textAlign="right" fontSize="0.875rem" whiteSpace="normal" wordBreak="break-word">
      {value}
    </Text>
  </Flex>
);

const PaymentDetailsContent = ({ details }: PaymentDetailsContentProps) => {
  const formatCurrency = useCurrencyFormatter();

  if (!details || details.length === 0) return null;

  const showPenalty = details.some((d) => d.penaltyValue > 0);

  return (
    <>
      {/* Small screens: card per detail item */}
      <VStack align="stretch" spacing={2} py={1} display={{ base: 'flex', md: 'none' }}>
        {details.map((detail, index) => (
          <Box key={index} borderRadius="md" bg="gray.50" px={4} py={3}>
            <VStack align="stretch" spacing={0}>
              <MobileRow label="Número de factura" value={detail.parentId} />
              <MobileRow label="Descripción" value={detail.description} />
              <MobileRow
                label="Fecha de vencimiento"
                value={new Date(detail.dueDate).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
              />
              <MobileRow label="Valor pendiente" value={formatCurrency(detail.outstandingValue)} />
              {detail.penaltyValue > 0 && (
                <MobileRow label="Sanción Incumplimiento" value={formatCurrency(detail.penaltyValue)} color="red.500" />
              )}
            </VStack>
          </Box>
        ))}
      </VStack>

      {/* Medium+ screens: shared-header table */}
      <Box display={{ base: 'none', md: 'block' }} py={2} px={2} bg="gray.50" borderRadius="md">
        <Table size={{ md: 'sm', xl: 'md' }} variant="unstyled">
          <Thead>
            <Tr>
              {['N. Factura', 'Descripción', 'Vencimiento'].map((header) => (
                <Th
                  key={header}
                  fontSize="0.7rem"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0.025em"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  whiteSpace="normal"
                  py={3}
                  px={{ md: 2, xl: 4 }}
                >
                  {header}
                </Th>
              ))}
              <Th
                fontSize="0.7rem"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.025em"
                borderBottom="1px solid"
                borderColor="gray.200"
                whiteSpace="normal"
                py={3}
                px={{ md: 2, xl: 4 }}
                isNumeric
              >
                Valor pendiente
              </Th>
              {showPenalty && (
                <Th
                  fontSize="0.7rem"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0.025em"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  whiteSpace="normal"
                  py={3}
                  px={{ md: 2, xl: 4 }}
                  isNumeric
                >
                  Sanción
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {details.map((detail, index) => (
              <Tr key={index}>
                <Td
                  fontSize="0.875rem"
                  color="gray.700"
                  py={3}
                  px={{ md: 2, xl: 4 }}
                  borderBottom={index < details.length - 1 ? '1px solid' : 'none'}
                  borderColor="gray.200"
                >
                  {detail.parentId}
                </Td>
                <Td
                  fontSize="0.875rem"
                  color="gray.700"
                  py={3}
                  px={{ md: 2, xl: 4 }}
                  borderBottom={index < details.length - 1 ? '1px solid' : 'none'}
                  borderColor="gray.200"
                  whiteSpace="normal"
                  wordBreak="break-word"
                >
                  {detail.description}
                </Td>
                <Td
                  fontSize="0.875rem"
                  color="gray.700"
                  py={3}
                  px={{ md: 2, xl: 4 }}
                  borderBottom={index < details.length - 1 ? '1px solid' : 'none'}
                  borderColor="gray.200"
                >
                  {new Date(detail.dueDate).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
                </Td>
                <Td
                  fontSize="0.875rem"
                  color="gray.700"
                  py={3}
                  px={{ md: 2, xl: 4 }}
                  borderBottom={index < details.length - 1 ? '1px solid' : 'none'}
                  borderColor="gray.200"
                  isNumeric
                >
                  {formatCurrency(detail.outstandingValue)}
                </Td>
                {showPenalty && (
                  <Td
                    fontSize="0.875rem"
                    color={detail.penaltyValue > 0 ? 'red.500' : 'gray.700'}
                    py={3}
                    px={{ md: 2, xl: 4 }}
                    borderBottom={index < details.length - 1 ? '1px solid' : 'none'}
                    borderColor="gray.200"
                    isNumeric
                  >
                    {detail.penaltyValue > 0 ? formatCurrency(detail.penaltyValue) : '-'}
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

export default PaymentDetailsContent;
